import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import type { AuthState } from "@/types";
import { authService } from "@/services/auth.service";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  configured: boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const initialState: AuthState = {
  session: null,
  authUser: null,
  usuario: null,
  loading: isSupabaseConfigured,
  profileError: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  const hydrateSession = useCallback(async (session: Session | null) => {
    if (!session?.user) {
      setState({ ...initialState, loading: false });
      return;
    }

    setState((current) => ({ ...current, session, authUser: session.user, loading: true, profileError: null }));
    try {
      const usuario = await authService.getUserProfile(session.user.id);
      setState({ session, authUser: session.user, usuario, loading: false, profileError: null });
    } catch {
      setState({
        session,
        authUser: session.user,
        usuario: null,
        loading: false,
        profileError: "Não foi possível carregar o perfil de acesso. Verifique as políticas do Supabase.",
      });
    }
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      return;
    }

    void supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        setState({ ...initialState, loading: false, profileError: "Não foi possível validar a sessão." });
        return;
      }
      void hydrateSession(data.session);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      window.setTimeout(() => void hydrateSession(session), 0);
    });

    return () => listener.subscription.unsubscribe();
  }, [hydrateSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!isSupabaseConfigured) throw new Error("Configure as variáveis do Supabase antes de entrar.");
    const { error } = await authService.signIn(email, password);
    if (error) throw error;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured) throw new Error("Configure as variáveis do Supabase antes de entrar.");
    const { error } = await authService.signInWithGoogle();
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await authService.signOut();
    if (error) throw error;
    setState({ ...initialState, loading: false });
  }, []);

  const refreshProfile = useCallback(async () => {
    if (state.authUser) await hydrateSession(state.session);
  }, [hydrateSession, state.authUser, state.session]);

  const value = useMemo(
    () => ({ ...state, signIn, signInWithGoogle, signOut, refreshProfile, configured: isSupabaseConfigured }),
    [state, signIn, signInWithGoogle, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
